import type { RevitFamily } from '../../../api/revit/types';
import { buildTextParameterTypeHints } from '../../revit/wizardParameterDataTypes';
import {
  areProjectParametersAvailableForBindingAndCategories,
  type AvailableProjectParameterDescriptor,
  type ParameterBindingKind,
} from '../../revit/projectParameterMutationPlan';
import {
  getFamilyParameterBindingKind,
  selectFamilyParameterMutationTargets,
} from '../../revit/familyParameterTargetSelection';
import { countUndoPlanEntries, countUndoPlanFamilies } from '../../parameterUndo/undoPlanCounts';
import { sanitizeRevitParameterNames } from '../../revitParameterNames';
import { type ConnectorParameterUndoEntry, type ConnectorParameterUndoPlan } from '../../../stores/useUIStore';
import {
  ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORY_OPTIONS,
  DEFAULT_ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORIES,
} from '../../../types';
import type { AdditionalCategoriesConfiguration, AdditionalCategoriesProjectParameterCategory } from '../../../types';
import { familyMatchesAnyObjectType } from './familyParameterAvailability';
import { getFormFieldValue } from './formFieldValues';
import { type AdditionalCategoryAdditionOperation } from './models';

export interface AdditionalCategoryTarget {
  targetObjectTypes: string[];
  parameterNames: string[];
  label: string;
}

export interface AdditionalCategoryBatchGroup {
  targetObjectTypes: string[];
  parameterNames: string[];
  labels: string[];
  targetFamilyIds?: string[];
  matchingFamilies?: RevitFamily[];
}

export type EvaluatedAdditionalCategoryTarget = AdditionalCategoryTarget & {
  matchingFamilies: RevitFamily[];
  compatibleFamilyCount: number;
  matchingFamilyCount: number;
  targetFamilyIds: string[];
};

export interface AdditionalCategoryFamilyTargetPlan {
  targetEvaluations: EvaluatedAdditionalCategoryTarget[];
  batchTargets: Array<
    AdditionalCategoryTarget & {
      targetFamilyIds?: string[];
      matchingFamilies?: RevitFamily[];
    }
  >;
  hasMatchingFamilies: boolean;
  allMatchingFamiliesSatisfied: boolean;
  usesBroadObjectTypeTargeting: boolean;
}

export function normalizeAdditionalCategoryProjectParameterCategories(
  categories: unknown,
): AdditionalCategoriesProjectParameterCategory[] {
  if (!Array.isArray(categories)) {
    return [...DEFAULT_ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORIES];
  }

  const allowedCategories = new Set<string>(
    ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORY_OPTIONS,
  );
  const normalizedCategories = Array.from(
    new Set(
      categories.filter(
        (category): category is AdditionalCategoriesProjectParameterCategory =>
          typeof category === 'string' && allowedCategories.has(category),
      ),
    ),
  );

  return normalizedCategories.length > 0
    ? normalizedCategories
    : [...DEFAULT_ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORIES];
}

export function buildAdditionalCategoryFamilyUndoPlan(
  targets: Array<
    AdditionalCategoryTarget & { matchingFamilies: RevitFamily[] }
  >,
  projectParameterNames: string[] = [],
): ConnectorParameterUndoPlan | null {
  const entriesByKey = new Map<string, ConnectorParameterUndoEntry>();

  targets.forEach((target) => {
    target.matchingFamilies.forEach((family) => {
      if (!family?.id || !family?.name) {
        return;
      }

      if (family.isEditable === false || family.isSystemFamily) {
        return;
      }

      target.parameterNames.forEach((parameterName) => {
        const previousBindingKind = getFamilyParameterBindingKind(
          family,
          parameterName,
        );
        entriesByKey.set(`${family.id}:${parameterName.trim().toLowerCase()}`, {
          familyId: family.id,
          familyName: family.name,
          parameterName,
          existedBefore: previousBindingKind !== null,
          ...(previousBindingKind ? { previousBindingKind } : {}),
        });
      });
    });
  });

  const entries = Array.from(entriesByKey.values());
  if (entries.length === 0 && projectParameterNames.length === 0) {
    return null;
  }

  // Counted from the entries, never from the families the targets matched: a
  // target that names no parameter leaves its families untouched.
  return {
    id: `additional-categories-undo-${Date.now()}-${Math.round(Math.random() * 1000)}`,
    createdAt: new Date().toISOString(),
    projectKey: '',
    projectParameterNames,
    familyCount: countUndoPlanFamilies({ entries }),
    entryCount: countUndoPlanEntries({ entries }),
    status: 'available',
    entries,
  };
}

export function collectAdditionalCategoryTargets(
  additionalCategoriesConfig: AdditionalCategoriesConfiguration,
): AdditionalCategoryTarget[] {
  const targets: AdditionalCategoryTarget[] = [];

  const valveParams = sanitizeRevitParameterNames([
    getFormFieldValue(additionalCategoriesConfig?.valveTagValue) || '',
  ]);
  if (valveParams.length > 0) {
    targets.push({
      label: 'VALVE',
      targetObjectTypes: ['VALVE'],
      parameterNames: valveParams,
    });
  }

  const supportParams = sanitizeRevitParameterNames([
    getFormFieldValue(additionalCategoriesConfig?.supportsTagValue) || '',
    getFormFieldValue(additionalCategoriesConfig?.supportsSsrefValue) || '',
  ]);
  if (supportParams.length > 0) {
    targets.push({
      label: 'SUPPORT',
      targetObjectTypes: ['SUPPORT', 'SUPPORTS'],
      parameterNames: supportParams,
    });
  }

  const instrumentParams = sanitizeRevitParameterNames([
    getFormFieldValue(additionalCategoriesConfig?.instrumentTagValue) || '',
  ]);
  if (instrumentParams.length > 0) {
    targets.push({
      label: 'INSTRUMENT',
      targetObjectTypes: ['INSTRUMENT'],
      parameterNames: instrumentParams,
    });
  }

  return targets;
}

export function collectAdditionalCategoryFamilySnapshotParameterNames(
  additionalCategoriesConfig: AdditionalCategoriesConfiguration,
  objectTypeParameterName: string,
): string[] {
  return sanitizeRevitParameterNames([
    ...collectAdditionalCategoryTargets(additionalCategoriesConfig).flatMap(
      (target) => target.parameterNames,
    ),
    objectTypeParameterName,
  ]);
}

export function buildAdditionalCategoryBatchGroups(
  targets: AdditionalCategoryTarget[],
): AdditionalCategoryBatchGroup[] {
  const groups = new Map<string, AdditionalCategoryBatchGroup>();

  targets.forEach((target) => {
    const targetObjectTypes = Array.from(
      new Set(
        target.targetObjectTypes.map((objectType) => objectType.trim()).filter(
          Boolean,
        ),
      ),
    ).sort((left, right) => left.localeCompare(right));
    const key = targetObjectTypes.join('|').toUpperCase();
    const existing = groups.get(key);
    const parameterNames = sanitizeRevitParameterNames(target.parameterNames);
    const targetWithFamilies = target as AdditionalCategoryTarget & {
      targetFamilyIds?: string[];
      matchingFamilies?: RevitFamily[];
    };
    const targetFamilyIds = Array.isArray(targetWithFamilies.targetFamilyIds) ? targetWithFamilies.targetFamilyIds : [];
    const matchingFamilies = Array.isArray(targetWithFamilies.matchingFamilies)
      ? targetWithFamilies.matchingFamilies
      : [];

    if (!existing) {
      groups.set(key, {
        targetObjectTypes,
        parameterNames,
        labels: [target.label],
        ...(targetFamilyIds.length > 0 ? { targetFamilyIds } : {}),
        ...(matchingFamilies.length > 0 ? { matchingFamilies } : {}),
      });
      return;
    }

    existing.parameterNames = sanitizeRevitParameterNames([
      ...existing.parameterNames,
      ...parameterNames,
    ]);
    existing.labels = Array.from(new Set([...existing.labels, target.label]));
    if (targetFamilyIds.length > 0) {
      existing.targetFamilyIds = Array.from(
        new Set([...(existing.targetFamilyIds ?? []), ...targetFamilyIds]),
      );
    }
    if (matchingFamilies.length > 0) {
      const familiesById = new Map(
        (existing.matchingFamilies ?? []).map((family) => [family.id, family]),
      );
      matchingFamilies.forEach((family) => {
        if (family.id) {
          familiesById.set(family.id, family);
        }
      });
      existing.matchingFamilies = Array.from(familiesById.values());
    }
  });

  return Array.from(groups.values());
}

export function selectAdditionalCategoryFamilyParameterTargets(
  targets: AdditionalCategoryTarget[],
  familySnapshot: RevitFamily[],
  bindingKind: ParameterBindingKind,
  objectTypeParameterName: string,
  snapshotAvailable = familySnapshot.length > 0,
  discriminatorBindingKind?: ParameterBindingKind,
): AdditionalCategoryFamilyTargetPlan {
  const hasReliableSnapshot = snapshotAvailable;
  const targetEvaluations: EvaluatedAdditionalCategoryTarget[] = targets.map(
    (target) => {
      const selection = selectFamilyParameterMutationTargets(
        familySnapshot,
        target.parameterNames,
        bindingKind,
        (family) => familyMatchesAnyObjectType(
          family,
          target.targetObjectTypes,
          objectTypeParameterName,
          discriminatorBindingKind,
        ),
        buildTextParameterTypeHints(target.parameterNames),
      );

      return {
        ...target,
        matchingFamilies: selection.targetFamilies,
        compatibleFamilyCount: selection.compatibleFamilyCount,
        matchingFamilyCount: selection.matchingFamilyCount,
        targetFamilyIds: selection.targetFamilyIds,
      };
    },
  );
  const explicitMutationTargets = targetEvaluations.filter(
    (target) => target.targetFamilyIds.length > 0,
  );
  const hasMatchingFamilies = targetEvaluations.some(
    (target) => target.matchingFamilyCount > 0,
  );
  const canDelegateUnmatchedTargets = !hasReliableSnapshot ||
    (familySnapshot.length > 0 && !hasMatchingFamilies);
  const unresolvedTargets = canDelegateUnmatchedTargets
    ? targetEvaluations.filter((target) => target.matchingFamilyCount === 0)
    : [];
  const allMatchingFamiliesSatisfied = targetEvaluations.length === 0 ||
    (
      hasMatchingFamilies &&
      targetEvaluations.every(
        (target) =>
          target.matchingFamilyCount === 0 ||
          target.targetFamilyIds.length === 0,
      )
    );

  if (explicitMutationTargets.length > 0 || unresolvedTargets.length > 0) {
    return {
      targetEvaluations,
      batchTargets: [...explicitMutationTargets, ...unresolvedTargets],
      hasMatchingFamilies,
      allMatchingFamiliesSatisfied,
      usesBroadObjectTypeTargeting: unresolvedTargets.length > 0,
    };
  }

  return {
    targetEvaluations,
    batchTargets: [],
    hasMatchingFamilies,
    allMatchingFamiliesSatisfied,
    usesBroadObjectTypeTargeting: false,
  };
}

export function buildAdditionalCategoryAdditionOperations(
  config: AdditionalCategoriesConfiguration,
): AdditionalCategoryAdditionOperation[] {
  const targets = collectAdditionalCategoryTargets(config);
  if (targets.length === 0) {
    return [];
  }

  const destination = config.parameterDestination ?? 'project';
  const bindingKind = resolveAdditionalCategoryParameterBindingKind(config);

  if (destination === 'family') {
    return buildAdditionalCategoryBatchGroups(targets).map((group) => ({
      destination: 'family',
      bindingKind,
      parameterNames: group.parameterNames,
      targetObjectTypes: group.targetObjectTypes,
      labels: group.labels,
    }));
  }

  return [
    {
      destination: 'project',
      bindingKind,
      categories: normalizeAdditionalCategoryProjectParameterCategories(
        config.projectParameterCategories,
      ),
      parameterNames: sanitizeRevitParameterNames(
        targets.flatMap((target) => target.parameterNames),
      ),
    },
  ];
}

export function resolveAdditionalCategoryParameterBindingKind(
  config: AdditionalCategoriesConfiguration,
): ParameterBindingKind {
  return config.parameterBindingKind ?? 'instance';
}

export function areAdditionalCategoryProjectParametersAvailable(
  config: AdditionalCategoriesConfiguration,
  availableProjectParameters: AvailableProjectParameterDescriptor[],
): boolean {
  const operations = buildAdditionalCategoryAdditionOperations(config);
  const projectOperations = operations.filter((operation) => operation.destination === 'project');

  if (projectOperations.length === 0) {
    return operations.length === 0;
  }

  return projectOperations.every((operation) =>
    areProjectParametersAvailableForBindingAndCategories(
      operation.parameterNames,
      operation.bindingKind,
      operation.categories,
      availableProjectParameters,
      buildTextParameterTypeHints(operation.parameterNames),
    )
  );
}
